import { useTranslation } from 'react-i18next';
import type { ThreadOrigin } from '../../types/agent';

/** A thread belongs to one workbook (sculpt-api PLAN-workbook-identity.md D9). Open from another one — an earlier
 *  version's thread, one from the "all" view, or the open thread after a "New version" answer — it is read-only,
 *  and the pane says so and offers to carry on here from a summary of it (§7). */
export function ReadOnlyBanner({ workbookName, continuing, error, onContinue }: { workbookName: string | null; continuing: boolean; error: string | null; onContinue: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1.5 border-b border-line-soft bg-hover-soft px-3.5 py-2 text-[12px]" role="status" data-testid="thread-read-only">
      <span className="text-ink">{workbookName ? t('thread.readOnly', { name: workbookName }) : t('thread.readOnlyNoFile')}</span>
      <div className="flex items-center gap-3">
        <button type="button" disabled={continuing} onClick={onContinue} className="rounded-[4px] bg-accent px-2 py-1 text-[11.5px] font-medium text-white hover:bg-accent-hover disabled:opacity-60">
          {continuing ? t('thread.continuing') : t('thread.continueHere')}
        </button>
        {error && <span role="alert" className="min-w-0 truncate text-danger">{t('thread.continueFailed', { error })}</span>}
      </div>
    </div>
  );
}

/** "Resumed from <title> · <file>": opens the thread this one was started from, read-only. */
export function ResumedHeader({ origin, onOpen }: { origin: ThreadOrigin; onOpen: (threadId: string) => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" onClick={() => onOpen(origin.thread_id)} data-testid="thread-resumed-from"
      className="w-full truncate border-b border-line-soft px-3.5 py-1.5 text-left text-[11.5px] text-muted hover:bg-hover-soft hover:text-ink">
      {t('thread.resumedFrom', { title: origin.title || t('history.untitled'), file: origin.workbook_name ?? '' })}
    </button>
  );
}
